import {
  ChevronDownIcon,
  Button,
  SingleSelectDropdown,
  SingleSelectDropdownItem,
} from "@stacklet/ui";

/** One grouping a directory can be arranged by. */
export interface GroupByOption<T extends string> {
  /** The grouping's stable id, also its `?group-by=` value. */
  id: T;
  /** The label shown in the dropdown. */
  label: string;
}

interface Props<T extends string> {
  /** The groupings on offer, in display order. */
  options: readonly GroupByOption<T>[];
  /** The currently selected grouping. */
  value: T;
  /** Called when a grouping is chosen. */
  onChange: (value: T) => void;
}

/**
 * The "Group by" toolbar dropdown shared by the RBAC directories (users,
 * groups): a tertiary button reporting the active grouping, opening a single
 * select list of the groupings on offer.
 */
export function GroupByDropdown<T extends string>({
  options,
  value,
  onChange,
}: Props<T>) {
  const active = options.find((option) => option.id === value) ?? options[0];

  return (
    <SingleSelectDropdown
      AnchorEle={
        <Button
          IconRight={<ChevronDownIcon size={12} />}
          aria-label="Group by"
          className="whitespace-nowrap py-xxs"
          variant="tertiary"
        >
          <span className="text-text-secondary">Group by:</span> {active.label}
        </Button>
      }
      placement="bottom right"
    >
      {options.map((option) => (
        <SingleSelectDropdownItem
          className="whitespace-nowrap"
          isSelected={option.id === value}
          key={option.id}
          onPress={() => onChange(option.id)}
        >
          {option.label}
        </SingleSelectDropdownItem>
      ))}
    </SingleSelectDropdown>
  );
}
